import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Storage } from '@ionic/storage';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { UserLoginModel } from '../models/User.model';
import { LanguageService } from '../services/language/language.service';
import { SharedService } from '../services/shared/shared.service';
import { UserService } from '../services/user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private storage: Storage,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private router: Router,
    private languageService: LanguageService,
    private userService: UserService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
    let token = '';
    let roles: string[] = [];
    let blocked = true;
    const requestedRoute = state.url;
    const redirectAfterLogin =
      ['/panel', '/retiree', '/qrcode', '/redirect'].filter(r => requestedRoute.startsWith(r)).length > 0;

    const userPortalRoleId = 'a36e1064-ab2f-47df-b416-98472edd2ccc';
    const userBackofficeRoleId = '6cf186e8-8977-4528-a92f-52643b8ddc64';
    const retiredEmployeeRoleId = '91281fe9-cd57-4865-8123-7a793c26261e';

    await lastValueFrom(this.userService.getMe()).then(
      async (res: UserLoginModel) => {
        await this.languageService.setLanguage(res.languageCode);

        roles = (res.roles as { id: string; name: string }[])
          .filter(
            r =>
              r.id === userPortalRoleId ||
              r.name === 'admin' ||
              r.id === userBackofficeRoleId ||
              r.id === retiredEmployeeRoleId,
          )
          .map(r => r.id);
        res.loginBlocked ? (blocked = true) : (blocked = false);
      },
      async () => {
        // Mantem o fluxo no guard para decidir o redirecionamento apos validar token/roles.
      },
    );

    await this.storage.get('token').then((res: string) => {
      token = res;
    });

    if (roles.length > 0 && !blocked) {
      const isRetiredEmployee = roles.includes(retiredEmployeeRoleId);
      if (isRetiredEmployee && !state.url.startsWith('/retiree/')) {
        this.router.navigateByUrl('/retiree');
        return false;
      } else if (state.url.startsWith('/retiree/') && !isRetiredEmployee) {
        this.router.navigateByUrl('/panel');
        return false;
      }
      return true;
    } else {
      if (!token) {
        this.router.navigateByUrl('/login');
        await this.storage.clear();

        if (redirectAfterLogin) {
          this.setPreviousRoute(requestedRoute);
        }

        return false;
      } else {
        if (roles.length === 0) {
          await this.storage.clear();

          if (redirectAfterLogin) {
            this.setPreviousRoute(requestedRoute);
          }

          this.router.navigateByUrl('/login');
          this.sharedService.showToast(this.translateService.instant('common.noPermissionUser'));
          this.sharedService.dismissLoading();

          return false;
        }
      }
    }
  }

  private setPreviousRoute(route: string): void {
    if (!route) {
      return;
    }

    const current = localStorage.getItem('previousRoute');
    const currentIsRedirect = current?.startsWith('/redirect/');
    const incomingIsRedirect = route.startsWith('/redirect/');

    // Nao sobrescreve um redirect existente com rotas genericas.
    if (currentIsRedirect && !incomingIsRedirect) {
      return;
    }

    localStorage.setItem('previousRoute', route);
  }
}
